/**
 * Wiki System JavaScript
 * Handles wiki navigation, search, and dynamic content loading
 */

class WikiSystem {
    constructor() {
        this.config = window.wikiConfig || {};
        this.wikiPages = [];
        this.currentPage = null;
        this.searchIndex = [];
        this.seoScriptIds = {
            article: 'wiki-article-jsonld',
            breadcrumbs: 'wiki-breadcrumbs-jsonld'
        };
        
        this.init();
    }

    async init() {
        await this.loadWikiPages();
        this.setupEventListeners();
        this.renderNavigation();
        this.setupSearch();
        
        if (this.config.currentPage) {
            this.setCurrentPage(this.config.currentPage);
        }
    }

    async loadWikiPages() {
        try {
            // Load wiki pages from Ghost API
            const response = await fetch(`${this.config.baseUrl}/ghost/api/content/posts/?key=${this.config.apiKey || this.config.ghostApiKey}&filter=tag:wiki&include=tags&limit=all`);
            const data = await response.json();
            
            this.wikiPages = data.posts || [];
            this.buildSearchIndex();
            
        } catch (error) {
            console.error('Failed to load wiki pages:', error);
            this.wikiPages = [];
        }
    }

    buildSearchIndex() {
        this.searchIndex = this.wikiPages.map(page => ({
            id: page.id,
            title: page.title,
            excerpt: page.excerpt,
            content: page.html,
            tags: page.tags?.map(tag => tag.name) || [],
            url: page.url,
            published_at: page.published_at
        }));
    }

    setupEventListeners() {
        // Search functionality
        const searchInput = document.getElementById('wiki-search');
        const searchBtn = document.getElementById('wiki-search-btn');
        
        if (searchInput) {
            searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value));
            searchInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    this.performSearch(e.target.value);
                }
            });
        }
        
        if (searchBtn) {
            searchBtn.addEventListener('click', () => {
                const query = searchInput?.value || '';
                this.performSearch(query);
            });
        }

        // Wiki index search
        const indexSearchInput = document.getElementById('wiki-index-search');
        const indexSearchBtn = document.getElementById('wiki-index-search-btn');
        
        if (indexSearchInput) {
            indexSearchInput.addEventListener('input', (e) => this.handleIndexSearch(e.target.value));
            indexSearchInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    this.performIndexSearch(e.target.value);
                }
            });
        }
        
        if (indexSearchBtn) {
            indexSearchBtn.addEventListener('click', () => {
                const query = indexSearchInput?.value || '';
                this.performIndexSearch(query);
            });
        }
    }

    renderNavigation() { this.renderTreeNavigation(); }

    renderTreeNavigation() {
        const navElement = document.getElementById('wiki-nav');
        if (!navElement) return;

        const tree = this.buildWikiTree(this.wikiPages);
        navElement.innerHTML = this.renderTreeHTML(tree, 0, []);

        // Normalize all groups to collapsed state to avoid stale states on navigation
        navElement.querySelectorAll('.wiki-tree-group').forEach((li) => {
            const toggle = li.querySelector(':scope > .wiki-tree-toggle');
            const child = li.querySelector(':scope > ul');
            if (toggle) toggle.setAttribute('aria-expanded', 'false');
            if (child) child.style.display = 'none';
        });

        // Expand nodes to current page
        this.expandToCurrentPage(navElement);

        // Toggle behavior (ensure single handler)
        if (this._navClickHandler) {
            try { navElement.removeEventListener('click', this._navClickHandler); } catch {}
        }
        this._navClickHandler = (e) => {
            const toggle = e.target.closest('.wiki-tree-toggle');
            if (!toggle || !navElement.contains(toggle)) return;
            e.preventDefault();
            e.stopPropagation();
            const li = toggle.closest('li');
            const child = li && li.querySelector(':scope > ul');
            if (!child) return;
            const expanded = toggle.getAttribute('aria-expanded') === 'true';
            const newExpanded = !expanded;
            toggle.setAttribute('aria-expanded', newExpanded ? 'true' : 'false');
            child.style.display = newExpanded ? 'block' : 'none';
        };
        navElement.addEventListener('click', this._navClickHandler);
    }

    buildWikiTree(pages) {
        const root = { __type: 'root', children: new Map(), pages: [] };
        const getOrCreate = (parent, segment) => {
            if (!parent.children.has(segment)) {
                parent.children.set(segment, { __type: 'group', name: segment, children: new Map(), pages: [] });
            }
            return parent.children.get(segment);
        };
        const toSegments = (url) => {
            const base = (this.config.baseUrl || '');
            const abs = url.startsWith('http') ? url.replace(base, '') : url;
            const trimmed = abs.replace(/^\/?/, '/').replace(/\/$/, '/').replace(/^\/wiki\//, '').replace(/\/$/, '');
            return trimmed ? trimmed.split('/') : [];
        };
        pages.forEach((page) => {
            const segments = toSegments(page.url);
            let cursor = root;
            for (let i = 0; i < segments.length; i += 1) {
                const segment = segments[i];
                if (i === segments.length - 1) {
                    // Leaf page under the last segment
                    cursor.pages.push(page);
                } else {
                    cursor = getOrCreate(cursor, segment);
                }
            }
            // If no segments (safety), attach to root
            if (segments.length === 0) root.pages.push(page);
        });
        return root;
    }

    renderTreeHTML(node, depth = 0, pathSegments = []) {
        const sortByName = (a, b) => a[0].localeCompare(b[0]);
        const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        const parts = [];
        const ulStyle = depth === 0 ? '' : ' style="display:none"';
        parts.push(`<ul class="wiki-tree depth-${depth}"${ulStyle}>`);

        // Render group children first
        const groups = Array.from(node.children?.entries?.() || []).sort(sortByName);
        groups.forEach(([segment, child]) => {
            const label = esc(segment.replace(/-/g, ' '));
            const isRootLevel = depth === 0;
            const expanded = false; // default collapsed for all levels
            const toggleChar = expanded ? '▾' : '▸';
            const accumulated = [...pathSegments, segment];
            const tagPath = accumulated.map(encodeURIComponent).join('/');
            const rawChildren = this.renderTreeHTML(child, depth + 1, accumulated);
            const childrenHTML = expanded ? rawChildren : rawChildren.replace('<ul ', '<ul style="display:none" ');
            parts.push(
                `<li class="wiki-tree-group">
                    <button class="wiki-tree-toggle" aria-expanded="${expanded ? 'true' : 'false'}" aria-label="Toggle ${label}">${toggleChar}</button>
                    <span class="wiki-tree-label"><a href="${(this.config.baseUrl || '')}/wiki/tag/${tagPath}/">${label}</a></span>
                    ${childrenHTML}
                </li>`
            );
        });

        // Then render pages attached at this level (as leaves)
        const leaves = (node.pages || []).slice().sort((a, b) => a.title.localeCompare(b.title));
        leaves.forEach((page) => {
            const isActive = this.config.currentPage === page.url;
            parts.push(
                `<li class="wiki-tree-leaf">
                    <a href="${page.url}" class="wiki-tree-link ${isActive ? 'active' : ''}">${esc(page.title || page.slug || page.url)}</a>
                </li>`
            );
        });

        parts.push('</ul>');
        return parts.join('');
    }

    expandToCurrentPage(navElement) {
        const current = this.config.currentPage;
        if (!current) return;
        const active = navElement.querySelector(`a[href="${current}"]`);
        if (!active) return;
        let node = active.parentElement;
        // Walk up and ensure ancestor ULs are visible and toggles marked expanded
        while (node && node !== navElement) {
            if (node.tagName === 'UL') {
                node.style.display = 'block';
            }
            if (node.classList && node.classList.contains('wiki-tree-group')) {
                const toggle = node.querySelector(':scope > .wiki-tree-toggle');
                const child = node.querySelector(':scope > ul');
                if (toggle && child) {
                    toggle.setAttribute('aria-expanded', 'true');
                    child.style.display = 'block';
                }
            }
            node = node.parentElement;
        }
    }

    groupPagesByCategory() {
        const categories = {};
        
        this.wikiPages.forEach(page => {
            const category = page.tags?.[0]?.name || 'Uncategorized';
            if (!categories[category]) {
                categories[category] = [];
            }
            categories[category].push(page);
        });
        
        return categories;
    }

    setupSearch() {
        // Initialize search functionality
        this.searchInput = document.getElementById('wiki-search');
        this.searchResults = [];
    }

    handleSearch(query) {
        if (!query.trim()) {
            this.clearSearchResults();
            return;
        }

        const results = this.searchPages(query);
        this.displaySearchResults(results);
    }

    performSearch(query) {
        if (!query.trim()) return;
        
        // Navigate to search results or first result
        const results = this.searchPages(query);
        if (results.length > 0) {
            window.location.href = results[0].url;
        }
    }

    handleIndexSearch(query) {
        if (!query.trim()) {
            this.showAllPages();
            return;
        }

        const results = this.searchPages(query);
        this.displayIndexSearchResults(results);
    }

    performIndexSearch(query) {
        if (!query.trim()) return;
        
        const results = this.searchPages(query);
        if (results.length > 0) {
            window.location.href = results[0].url;
        }
    }

    searchPages(query) {
        const searchTerm = query.toLowerCase();
        
        return this.searchIndex.filter(page => {
            return page.title.toLowerCase().includes(searchTerm) ||
                   page.excerpt.toLowerCase().includes(searchTerm) ||
                   page.content.toLowerCase().includes(searchTerm) ||
                   page.tags.some(tag => tag.toLowerCase().includes(searchTerm));
        }).sort((a, b) => {
            // Prioritize title matches
            const aTitleMatch = a.title.toLowerCase().includes(searchTerm);
            const bTitleMatch = b.title.toLowerCase().includes(searchTerm);
            
            if (aTitleMatch && !bTitleMatch) return -1;
            if (!aTitleMatch && bTitleMatch) return 1;
            
            return new Date(b.published_at) - new Date(a.published_at);
        });
    }

    displaySearchResults(results) {
        // This would be implemented for dropdown search results
        // For now, we'll just highlight matching pages in navigation
        this.highlightSearchResults(results);
    }

        displayIndexSearchResults(results) {
        const recentGrid = document.getElementById('wiki-recent-grid');
        const pagesList = document.getElementById('wiki-pages-list');
        
        if (recentGrid) {
            recentGrid.innerHTML = this.renderSearchResultsGrid(results);
        }
        
        if (pagesList) {
                pagesList.innerHTML = this.renderSearchResultsList(results);
        }
    }

    renderSearchResultsGrid(results) {
        if (results.length === 0) {
            return '<p class="wiki-no-results">No pages found matching your search.</p>';
        }

        return results.slice(0, 6).map(page => `
            <a href="${page.url}" class="wiki-page-card">
                <h3>${this.highlightSearchTerm(page.title)}</h3>
                <p>${this.highlightSearchTerm(page.excerpt)}</p>
                <div class="wiki-page-meta">
                    ${page.tags.map(tag => `<span class="wiki-tag" role="link" tabindex="0" onclick="event.preventDefault(); event.stopPropagation(); window.location.href='${(this.config.baseUrl || '').replace(/\/$/, '')}/wiki/tag/${encodeURIComponent(tag)}/'" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault(); event.stopPropagation(); window.location.href='${(this.config.baseUrl || '').replace(/\/$/, '')}/wiki/tag/${encodeURIComponent(tag)}/'}">${tag}</span>`).join('')}
                </div>
            </a>
        `).join('');
    }

    renderSearchResultsList(results) {
        if (results.length === 0) {
            return '<p class="wiki-no-results">No pages found matching your search.</p>';
        }

        return results.map(page => `
            <a href="${page.url}" class="wiki-page-item">
                <h3>${this.highlightSearchTerm(page.title)}</h3>
                <div class="wiki-page-right">
                    <span class="wiki-page-date">${new Date(page.published_at).toLocaleDateString()}</span>
                    <div class="wiki-tags">
                        ${(page.tags || []).map(tag => `<span class="wiki-tag" role="link" tabindex="0" onclick="event.preventDefault(); event.stopPropagation(); window.location.href='${(this.config.baseUrl || '').replace(/\/$/, '')}/wiki/tag/${encodeURIComponent(tag)}/'" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault(); event.stopPropagation(); window.location.href='${(this.config.baseUrl || '').replace(/\/$/, '')}/wiki/tag/${encodeURIComponent(tag)}/'}">${tag}</span>`).join('')}
                    </div>
                </div>
            </a>
        `).join('');
    }

    highlightSearchTerm(text) {
        const searchTerm = this.searchInput?.value || '';
        if (!searchTerm) return text;
        
        const regex = new RegExp(`(${searchTerm})`, 'gi');
        return text.replace(regex, '<mark>$1</mark>');
    }

    highlightSearchResults(results) {
        // Remove existing highlights
        document.querySelectorAll('.wiki-tree-link').forEach(link => {
            link.classList.remove('search-highlight');
        });
        
        // Add highlights to matching pages
        results.forEach(result => {
            const link = document.querySelector(`.wiki-tree-link[href="${result.url}"]`);
            if (link) {
                link.classList.add('search-highlight');
            }
        });
    }

    clearSearchResults() {
        document.querySelectorAll('.wiki-tree-link').forEach(link => {
            link.classList.remove('search-highlight');
        });
    }

    showAllPages() {
        const recentGrid = document.getElementById('wiki-recent-grid');
        const pagesList = document.getElementById('wiki-pages-list');
        
        if (recentGrid) {
            recentGrid.innerHTML = this.renderRecentPages();
        }
        
        if (pagesList) {
            pagesList.innerHTML = this.renderAllPages();
        }
    }

    renderRecentPages() {
        const recentPages = this.wikiPages
            .sort((a, b) => new Date(b.published_at) - new Date(a.published_at))
            .slice(0, 6);
            
        return recentPages.map(page => `
            <a href="${page.url}" class="wiki-page-card">
                <h3>${page.title}</h3>
                <p>${page.excerpt}</p>
                <div class="wiki-page-meta">
                    ${page.tags.map(tag => `<span class="wiki-tag" role="link" tabindex="0" onclick="event.preventDefault(); event.stopPropagation(); window.location.href='${(this.config.baseUrl || '').replace(/\/$/, '')}/wiki/tag/${encodeURIComponent(tag)}/'" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault(); event.stopPropagation(); window.location.href='${(this.config.baseUrl || '').replace(/\/$/, '')}/wiki/tag/${encodeURIComponent(tag)}/'}">${tag}</span>`).join('')}
                </div>
            </a>
        `).join('');
    }

        renderAllPages() {
        return this.wikiPages
            .sort((a, b) => a.title.localeCompare(b.title))
            .map(page => `
                <a href="${page.url}" class="wiki-page-item">
                    <h3>${page.title}</h3>
                    <div class="wiki-page-right">
                        <span class="wiki-page-date">${new Date(page.published_at).toLocaleDateString()}</span>
                        <div class="wiki-tags">
                            ${(page.tags || []).map(tag => `<span class="wiki-tag" role="link" tabindex="0" onclick="event.preventDefault(); event.stopPropagation(); window.location.href='${(this.config.baseUrl || '').replace(/\/$/, '')}/wiki/tag/${encodeURIComponent(tag)}/'" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault(); event.stopPropagation(); window.location.href='${(this.config.baseUrl || '').replace(/\/$/, '')}/wiki/tag/${encodeURIComponent(tag)}/'}">${tag}</span>`).join('')}
                        </div>
                    </div>
                </a>
            `).join('');
    }

    setCurrentPage(url) {
        this.currentPage = url;
        this.updateBreadcrumb();
        this.updateNavigation();
        this.updatePrevNextLinks();
        this.updateSeoForCurrent();
    }

    updateBreadcrumb() {
        const breadcrumbElement = document.getElementById('wiki-breadcrumb');
        if (!breadcrumbElement || !this.currentPage) return;

        const currentPage = this.wikiPages.find(page => page.url === this.currentPage);
        if (!currentPage) return;

        // Build breadcrumb from URL segments under /wiki/
        const base = (this.config.baseUrl || '').replace(/\/$/, '');
        const toSegments = (url) => {
            const abs = url.startsWith('http') ? url.replace(base, '') : url;
            const trimmed = abs.replace(/^\/?/, '/').replace(/\/$/, '/').replace(/^\/wiki\//, '').replace(/\/$/, '');
            return trimmed ? trimmed.split('/') : [];
        };
        const labelize = (s) => s.replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
        const segments = toSegments(currentPage.url);

        const parts = [];
        parts.push(`<a href="${base}/wiki/">Wiki</a>`);
        let acc = [];
        for (let i = 0; i < segments.length - 1; i += 1) {
            acc.push(segments[i]);
            const seg = segments[i];
            parts.push('<span class="wiki-breadcrumb-separator">/</span>');
            parts.push(`<a href="${base}/wiki/tag/${encodeURIComponent(seg)}/">${labelize(seg)}</a>`);
        }
        if (segments.length > 0) {
            parts.push('<span class="wiki-breadcrumb-separator">/</span>');
        }
        parts.push(`<span>${currentPage.title}</span>`);
        breadcrumbElement.innerHTML = parts.join('');
    }

    // ---------- SEO Utilities ----------
    setMetaTag(nameOrProperty, content, { property = false } = {}) {
        if (!content) return;
        const selector = property ? `meta[property="${nameOrProperty}"]` : `meta[name="${nameOrProperty}"]`;
        let tag = document.head.querySelector(selector);
        if (!tag) {
            tag = document.createElement('meta');
            if (property) tag.setAttribute('property', nameOrProperty); else tag.setAttribute('name', nameOrProperty);
            document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
    }

    setLinkTag(rel, href) {
        if (!href) return;
        let link = document.head.querySelector(`link[rel="${rel}"]`);
        if (!link) {
            link = document.createElement('link');
            link.setAttribute('rel', rel);
            document.head.appendChild(link);
        }
        link.setAttribute('href', href);
    }

    appendMetaProperty(propertyName, content) {
        if (!content) return;
        const tag = document.createElement('meta');
        tag.setAttribute('property', propertyName);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
    }

    injectJsonLd(id, data) {
        if (!data) return;
        let script = document.getElementById(id);
        if (!script) {
            script = document.createElement('script');
            script.type = 'application/ld+json';
            script.id = id;
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(data);
    }

    toAbsoluteUrl(url) {
        if (!url) return '';
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        const base = (this.config.baseUrl || '').replace(/\/$/, '');
        const path = url.startsWith('/') ? url : `/${url}`;
        return `${base}${path}`;
    }

    updateSeoForCurrent() {
        if (!this.currentPage) return;
        const page = this.wikiPages.find(p => p.url === this.currentPage);
        if (!page) return;
        const siteTitle = this.config.siteTitle || '';
        const siteLocale = this.config.siteLocale || 'en';
        const pageTitle = page.title ? `${page.title} | ${siteTitle}` : siteTitle;
        const rawDescription = page.meta_description || page.excerpt || (page.html || '').replace(/<[^>]+>/g, '').slice(0, 300);
        const description = rawDescription ? rawDescription.trim().replace(/\s+/g, ' ').slice(0, 160) : '';
        const url = this.toAbsoluteUrl(page.url);
        const image = this.toAbsoluteUrl(page.feature_image);
        const keywords = Array.isArray(page.tags) ? page.tags.map(t => t.name).join(', ') : '';
        const siteTwitter = (this.config.siteTwitter || '').toString().replace(/^@/, '');
        const ogLocale = siteLocale ? siteLocale.replace('-', '_') : undefined;
        const publishedISO = page.published_at ? new Date(page.published_at).toISOString() : undefined;
        const modifiedISO = page.updated_at ? new Date(page.updated_at).toISOString() : undefined;

        if (pageTitle) document.title = pageTitle;
        this.setLinkTag('canonical', url);
        this.setMetaTag('description', description);
        this.setMetaTag('keywords', keywords);
        this.setMetaTag('og:title', page.title || siteTitle, { property: true });
        this.setMetaTag('og:description', description, { property: true });
        this.setMetaTag('og:type', 'article', { property: true });
        this.setMetaTag('og:url', url, { property: true });
        if (image) this.setMetaTag('og:image', image, { property: true });
        if (siteTitle) this.setMetaTag('og:site_name', siteTitle, { property: true });
        if (ogLocale) this.setMetaTag('og:locale', ogLocale, { property: true });
        if (publishedISO) this.setMetaTag('article:published_time', publishedISO, { property: true });
        if (modifiedISO) this.setMetaTag('article:modified_time', modifiedISO, { property: true });
        // Repeatable article:tag
        try {
            document.head.querySelectorAll('meta[property="article:tag"]').forEach(n => n.parentNode && n.parentNode.removeChild(n));
        } catch {}
        if (Array.isArray(page.tags)) {
            page.tags.forEach(t => {
                const name = typeof t === 'string' ? t : (t && t.name);
                if (name) this.appendMetaProperty('article:tag', name);
            });
        }
        this.setMetaTag('twitter:card', image ? 'summary_large_image' : 'summary');
        this.setMetaTag('twitter:title', page.title || siteTitle);
        this.setMetaTag('twitter:description', description);
        if (image) this.setMetaTag('twitter:image', image);
        if (siteTwitter) this.setMetaTag('twitter:site', `@${siteTwitter}`);

        // JSON-LD
        const articleJson = {
            '@context': 'https://schema.org',
            '@type': 'Article',
            mainEntityOfPage: { '@type': 'WebPage', '@id': url },
            headline: page.title,
            description,
            datePublished: page.published_at ? new Date(page.published_at).toISOString().slice(0, 10) : undefined,
            dateModified: page.updated_at ? new Date(page.updated_at).toISOString().slice(0, 10) : undefined,
            image: image ? [image] : undefined,
            inLanguage: siteLocale,
            keywords,
            publisher: { '@type': 'Organization', '@id': `${(this.config.baseUrl || '').replace(/\/$/, '')}#org` },
            speakable: { '@type': 'SpeakableSpecification', cssSelector: ['article h1', 'article p'] }
        };
        Object.keys(articleJson).forEach(k => articleJson[k] === undefined && delete articleJson[k]);
        this.injectJsonLd(this.seoScriptIds.article, articleJson);

        const siteUrl = (this.config.baseUrl || '').replace(/\/$/, '');
        const category = page.tags?.[0]?.name || 'Wiki';
        const items = [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
            { '@type': 'ListItem', position: 2, name: 'Wiki', item: `${siteUrl}/wiki/` }
        ];
        if (category && category !== 'Wiki') {
            items.push({ '@type': 'ListItem', position: 3, name: category, item: `${siteUrl}/wiki/tag/${encodeURIComponent(category)}/` });
            items.push({ '@type': 'ListItem', position: 4, name: page.title, item: url });
        } else {
            items.push({ '@type': 'ListItem', position: 3, name: page.title, item: url });
        }
        this.injectJsonLd(this.seoScriptIds.breadcrumbs, {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: items
        });
    }

    updateNavigation() {
        // Update active state in navigation
        document.querySelectorAll('.wiki-tree-link').forEach(link => {
            link.classList.remove('active');
            if (link.getAttribute('href') === this.currentPage) {
                link.classList.add('active');
            }
        });
    }

    updatePrevNextLinks() {
        const currentIndex = this.wikiPages.findIndex(page => page.url === this.currentPage);
        if (currentIndex === -1) return;

        const prevElement = document.getElementById('wiki-nav-prev');
        const nextElement = document.getElementById('wiki-nav-next');

        if (prevElement) {
            if (currentIndex > 0) {
                const prevPage = this.wikiPages[currentIndex - 1];
                prevElement.innerHTML = `
                    <a href="${prevPage.url}" class="wiki-nav-link-btn prev">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="15,18 9,12 15,6"></polyline>
                        </svg>
                        ${prevPage.title}
                    </a>
                `;
            } else {
                prevElement.innerHTML = '';
            }
        }

        if (nextElement) {
            if (currentIndex < this.wikiPages.length - 1) {
                const nextPage = this.wikiPages[currentIndex + 1];
                nextElement.innerHTML = `
                    <a href="${nextPage.url}" class="wiki-nav-link-btn next">
                        ${nextPage.title}
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="9,18 15,12 9,6"></polyline>
                        </svg>
                    </a>
                `;
            } else {
                nextElement.innerHTML = '';
            }
        }
    }
}

// Wiki Index System
class WikiIndexSystem {
    constructor() {
        this.config = window.wikiIndexConfig || {};
        this.wikiPages = [];
        this.categories = [];
        this.activeCategoryFilter = null;
        this.seoScriptIds = {
            index: 'wiki-index-jsonld',
            breadcrumbs: 'wiki-index-breadcrumbs-jsonld'
        };
        
        this.init();
    }

    async init() {
        await this.loadWikiPages();
        this.setupEventListeners();
        this.renderCategories();
        // Also render the tree navigation on the index page sidebar
        this.renderTreeNavigation();
        const activeTag = this.config.activeTag;
        const activeTagPath = this.config.activeTagPath;
        if (activeTag) {
            const normalized = String(activeTag).toLowerCase();
            let filtered = this.wikiPages.filter(p => {
                const tags = Array.isArray(p.tags) ? p.tags : [];
                return tags.some(t => String(t).toLowerCase() === normalized);
            });
            if (filtered.length === 0 && activeTagPath) {
                // Try to match hierarchical tag paths stored in tags (exact or prefix)
                const pathNorm = String(activeTagPath).toLowerCase();
                filtered = this.wikiPages.filter(p => {
                    const tags = Array.isArray(p.tags) ? p.tags : [];
                    return tags.some(t => {
                        const s = String(t).toLowerCase();
                        return s === pathNorm || s.startsWith(pathNorm + '/');
                    });
                });
            }
            if (filtered.length === 0) {
                filtered = this.wikiPages.filter(p => {
                    const tags = Array.isArray(p.tags) ? p.tags : [];
                    return tags.some(t => String(t).toLowerCase().includes(normalized));
                });
            }
            this.updatePagesList(filtered);
            if (filtered.length === 0) {
                const list = document.getElementById('wiki-pages-list');
                if (list) {
                    list.innerHTML = `<p class="wiki-no-results">No wiki pages found with the tag "${activeTagPath || activeTag}" yet.</p>`;
                }
            }
        } else {
            this.renderRecentPages();
            this.renderAllPages();
        }

        // Inject wiki-index.md content if present
        this.injectWikiIndexMarkdown();

        // Update SEO for index / tag / category context
        this.updateSeoForIndexContext();
    }

    renderTreeNavigation() {
        const navElement = document.getElementById('wiki-nav');
        if (!navElement) return;

        const tree = this.buildWikiTree(this.wikiPages);
        navElement.innerHTML = this.renderTreeHTML(tree, 0, []);

        // Normalize all groups to collapsed state to avoid stale states on navigation
        navElement.querySelectorAll('.wiki-tree-group').forEach((li) => {
            const toggle = li.querySelector(':scope > .wiki-tree-toggle');
            const child = li.querySelector(':scope > ul');
            if (toggle) toggle.setAttribute('aria-expanded', 'false');
            if (child) child.style.display = 'none';
        });

        // Expand nodes to current page if available
        this.expandToCurrentPage(navElement);

        // Toggle behavior (ensure single handler)
        if (this._indexNavClickHandler) {
            try { navElement.removeEventListener('click', this._indexNavClickHandler); } catch {}
        }
        this._indexNavClickHandler = (e) => {
            const toggle = e.target.closest('.wiki-tree-toggle');
            if (!toggle || !navElement.contains(toggle)) return;
            e.preventDefault();
            e.stopPropagation();
            const li = toggle.closest('li');
            const child = li && li.querySelector(':scope > ul');
            if (!child) return;
            const expanded = toggle.getAttribute('aria-expanded') === 'true';
            const newExpanded = !expanded;
            toggle.setAttribute('aria-expanded', newExpanded ? 'true' : 'false');
            child.style.display = newExpanded ? 'block' : 'none';
        };
        navElement.addEventListener('click', this._indexNavClickHandler);
    }

    buildWikiTree(pages) {
        const root = { __type: 'root', children: new Map(), pages: [] };
        const getOrCreate = (parent, segment) => {
            if (!parent.children.has(segment)) {
                parent.children.set(segment, { __type: 'group', name: segment, children: new Map(), pages: [] });
            }
            return parent.children.get(segment);
        };
        const toSegments = (url) => {
            const base = (this.config.baseUrl || '');
            const abs = url.startsWith('http') ? url.replace(base, '') : url;
            const trimmed = abs.replace(/^\/?/, '/').replace(/\/$/, '/').replace(/^\/wiki\//, '').replace(/\/$/, '');
            return trimmed ? trimmed.split('/') : [];
        };
        pages.forEach((page) => {
            const segments = toSegments(page.url);
            let cursor = root;
            for (let i = 0; i < segments.length; i += 1) {
                const segment = segments[i];
                if (i === segments.length - 1) {
                    cursor.pages.push(page);
                } else {
                    cursor = getOrCreate(cursor, segment);
                }
            }
            if (segments.length === 0) root.pages.push(page);
        });
        return root;
    }

    renderTreeHTML(node, depth = 0, pathSegments = []) {
        const sortByName = (a, b) => a[0].localeCompare(b[0]);
        const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        const parts = [];
        const ulStyle = depth === 0 ? '' : ' style="display:none"';
        parts.push(`<ul class="wiki-tree depth-${depth}"${ulStyle}>`);

        // Groups first
        const groups = Array.from(node.children?.entries?.() || []).sort(sortByName);
        groups.forEach(([segment, child]) => {
            const label = esc(segment.replace(/-/g, ' '));
            const isRootLevel = depth === 0;
            const expanded = false; // default collapsed for all levels
            const toggleChar = expanded ? '▾' : '▸';
            const accumulated = [...pathSegments, segment];
            const tagPath = accumulated.map(encodeURIComponent).join('/');
            const rawChildren = this.renderTreeHTML(child, depth + 1, accumulated);
            const childrenHTML = expanded ? rawChildren : rawChildren.replace('<ul ', '<ul style="display:none" ');
            parts.push(
                `<li class="wiki-tree-group">
                    <button class="wiki-tree-toggle" aria-expanded="${expanded ? 'true' : 'false'}" aria-label="Toggle ${label}">${toggleChar}</button>
                    <span class="wiki-tree-label"><a href="${(this.config.baseUrl || '')}/wiki/tag/${tagPath}/">${label}</a></span>
                    ${childrenHTML}
                </li>`
            );
        });

        // Leaves
        const leaves = (node.pages || []).slice().sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        leaves.forEach((page) => {
            parts.push(
                `<li class="wiki-tree-leaf">
                    <a href="${page.url}" class="wiki-tree-link">${esc(page.title || page.slug || page.url)}</a>
                </li>`
            );
        });

        parts.push('</ul>');
        return parts.join('');
    }

    expandToCurrentPage(navElement) {
        const current = (this.config && this.config.currentPage) || '';
        if (!current) return;
        const active = navElement.querySelector(`a[href="${current}"]`);
        if (!active) return;
        let node = active.parentElement;
        while (node && node !== navElement) {
            if (node.tagName === 'UL') {
                node.style.display = 'block';
            }
            if (node.classList && node.classList.contains('wiki-tree-group')) {
                const toggle = node.querySelector(':scope > .wiki-tree-toggle');
                const child = node.querySelector(':scope > ul');
                if (toggle && child) {
                    toggle.setAttribute('aria-expanded', 'true');
                    child.style.display = 'block';
                }
            }
            node = node.parentElement;
        }
    }

    async loadWikiPages() {
        // Try markdown data first (resilient URL handling + cache-busting)
        if (this.config.dataUrl) {
            const urlsToTry = [];
            const raw = String(this.config.dataUrl || '');
            const hasQuery = raw.includes('?');
            const cacheBust = `${hasQuery ? '&' : '?'}v=${Date.now()}`;
            urlsToTry.push(raw + cacheBust);
            if (raw.startsWith('/')) {
                const base = (this.config.baseUrl || '').replace(/\/$/, '');
                if (base) urlsToTry.push(base + raw + cacheBust);
            }

            for (const url of urlsToTry) {
                try {
                    const response = await fetch(url, { credentials: 'same-origin' });
                    if (response.ok) {
                        const data = await response.json();
                        this.wikiPages = (data.pages || []).map(p => ({
                            ...p,
                            tags: Array.isArray(p.tags) ? p.tags : []
                        }));
                        this.buildCategories();
                        return;
                    }
                } catch (_) {
                    // Try next URL variant
                }
            }
        }

        // Fallback to Ghost API
        try {
            const response = await fetch(`${this.config.ghostApiUrl}?key=${this.config.ghostApiKey}&filter=tag:wiki&include=tags&limit=all`);
            const data = await response.json();
            
            this.wikiPages = (data.posts || []).map(p => ({
                ...p,
                tags: (p.tags || []).map(t => t.name)
            }));
            this.buildCategories();
            
        } catch (error) {
            console.error('Failed to load wiki pages:', error);
            this.wikiPages = [];
        }

        // If still empty, render friendly empty state
        if (!Array.isArray(this.wikiPages) || this.wikiPages.length === 0) {
            this.renderEmptyState();
        }
    }

    // ---------- SEO Utilities (Index) ----------
    setMetaTag(nameOrProperty, content, { property = false } = {}) {
        if (!content) return;
        const selector = property ? `meta[property="${nameOrProperty}"]` : `meta[name="${nameOrProperty}"]`;
        let tag = document.head.querySelector(selector);
        if (!tag) {
            tag = document.createElement('meta');
            if (property) tag.setAttribute('property', nameOrProperty); else tag.setAttribute('name', nameOrProperty);
            document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
    }

    setLinkTag(rel, href) {
        if (!href) return;
        let link = document.head.querySelector(`link[rel="${rel}"]`);
        if (!link) {
            link = document.createElement('link');
            link.setAttribute('rel', rel);
            document.head.appendChild(link);
        }
        link.setAttribute('href', href);
    }

    injectJsonLd(id, data) {
        if (!data) return;
        let script = document.getElementById(id);
        if (!script) {
            script = document.createElement('script');
            script.type = 'application/ld+json';
            script.id = id;
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(data);
    }

    toAbsoluteUrl(path) {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        const base = (this.config.baseUrl || '').replace(/\/$/, '');
        const normalized = path.startsWith('/') ? path : `/${path}`;
        return `${base}${normalized}`;
    }

    updateSeoForIndexContext() {
        const siteTitle = this.config.siteTitle || '';
        const siteLocale = this.config.siteLocale || 'en';
        const baseUrl = (this.config.baseUrl || '').replace(/\/$/, '');
        const isTag = !!this.config.activeTag;
        const isCategory = !!this.config.activeCategory;
        const contextName = isTag ? (this.config.activeTagPath || this.config.activeTag) : (isCategory ? this.config.activeCategory : 'Wiki');
        const title = (isTag
            ? `Wiki: ${contextName} | ${siteTitle}`
            : (isCategory ? `Category: ${contextName} | ${siteTitle}` : `Wiki Documentation | ${siteTitle}`));
        const description = isTag
            ? `Pages tagged with "${contextName}" in the wiki.`
            : (isCategory ? `Pages in category "${contextName}" in the wiki.` : (this.config.siteDescription || 'Browse our comprehensive documentation and guides'));
        const url = isTag
            ? `${baseUrl}/wiki/tag/${(this.config.activeTagPath || encodeURIComponent(this.config.activeTag))}/`
            : (isCategory ? `${baseUrl}/wiki/category/${encodeURIComponent(contextName)}/` : `${baseUrl}/wiki/`);
        const siteTwitter = (this.config.siteTwitter || '').toString().replace(/^@/, '');
        const ogLocale = siteLocale ? siteLocale.replace('-', '_') : undefined;

        // Title & Canonical
        if (title) document.title = title;
        this.setLinkTag('canonical', url);

        // Meta
        this.setMetaTag('description', description);
        this.setMetaTag('og:title', title, { property: true });
        this.setMetaTag('og:description', description, { property: true });
        this.setMetaTag('og:type', 'website', { property: true });
        this.setMetaTag('og:url', url, { property: true });
        if (siteTitle) this.setMetaTag('og:site_name', siteTitle, { property: true });
        if (ogLocale) this.setMetaTag('og:locale', ogLocale, { property: true });
        this.setMetaTag('twitter:card', 'summary');
        this.setMetaTag('twitter:title', title);
        this.setMetaTag('twitter:description', description);
        if (siteTwitter) this.setMetaTag('twitter:site', `@${siteTwitter}`);

        // ItemList JSON-LD
        const items = (this.wikiPages || []).slice(0, 20).map((p, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            url: this.toAbsoluteUrl(p.url),
            name: p.title
        }));
        const collectionJson = {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: title,
            description,
            inLanguage: siteLocale,
            url,
            mainEntity: {
                '@type': 'ItemList',
                itemListElement: items
            }
        };
        this.injectJsonLd(this.seoScriptIds.index, collectionJson);

        // Breadcrumbs
        const crumbs = [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${baseUrl}/` },
            { '@type': 'ListItem', position: 2, name: 'Wiki', item: `${baseUrl}/wiki/` }
        ];
        if (isTag) {
            const segments = Array.isArray(this.config.activeTagSegments) ? this.config.activeTagSegments : [this.config.activeTag];
            let acc = [];
            segments.forEach((seg, idx) => {
                acc.push(seg);
                const name = (seg || '').replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
                const segUrl = `${baseUrl}/wiki/tag/${acc.map(encodeURIComponent).join('/')}/`;
                crumbs.push({ '@type': 'ListItem', position: 3 + idx, name, item: segUrl });
            });
        } else if (isCategory) {
            crumbs.push({ '@type': 'ListItem', position: 3, name: this.config.activeCategory, item: url });
        }
        this.injectJsonLd(this.seoScriptIds.breadcrumbs, {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: crumbs
        });
    }

    buildCategories() {
        // Build a tag cloud from all tags across pages (ignore Uncategorized)
        const tagCountMap = {};

        this.wikiPages.forEach(page => {
            const pageTags = Array.isArray(page.tags) ? page.tags : [];
            pageTags.forEach(tag => {
                const tagName = typeof tag === 'string' ? tag : tag?.name;
                if (!tagName) return;
                tagCountMap[tagName] = (tagCountMap[tagName] || 0) + 1;
            });
        });

        const tagArray = Object.entries(tagCountMap)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => (b.count - a.count) || a.name.localeCompare(b.name));

        // Keep only the top 20 by frequency
        this.categories = tagArray.slice(0, 20);
    }

    setupEventListeners() {
        // Category filtering
        document.addEventListener('click', (e) => {
            const el = e.target.closest('.wiki-category');
            if (el) {
                e.preventDefault();
                const category = el.getAttribute('data-category');
                // Toggle behavior based on current active
                if (this.activeCategoryFilter && this.activeCategoryFilter === category) {
                    this.clearCategoryFilter();
                } else {
                    this.filterByCategory(category);
                }
            }
        });
    }

    renderCategories() {
        const categoriesElement = document.getElementById('wiki-categories');
        if (!categoriesElement) return;

        if (!this.categories || this.categories.length === 0) {
            categoriesElement.innerHTML = '';
            return;
        }

        // Determine min/max counts for sizing
        const counts = this.categories.map(c => c.count);
        const minCount = Math.min(...counts);
        const maxCount = Math.max(...counts);
        const minSize = 1.2; // rem (increased for readability)
        const maxSize = 1.6; // rem

        const sizeForCount = (count) => {
            if (maxCount === minCount) return (minSize + maxSize) / 2;
            const ratio = (count - minCount) / (maxCount - minCount);
            return +(minSize + ratio * (maxSize - minSize)).toFixed(2);
        };

        const categoriesHTML = this.categories.map(category => {
            const size = sizeForCount(category.count);
            return `
            <a href="${this.config.baseUrl}/wiki/tag/${encodeURIComponent(category.name)}/" class="wiki-category" data-category="${category.name}" style="font-size: ${size}rem" title="${category.count} page${category.count === 1 ? '' : 's'}">
                ${category.name}
            </a>`;
        }).join('');

        categoriesElement.innerHTML = categoriesHTML;
    }

    renderRecentPages() {
        const recentGrid = document.getElementById('wiki-recent-grid');
        if (!recentGrid) return;

        const recentPages = this.wikiPages
            .sort((a, b) => new Date(b.published_at) - new Date(a.published_at))
            .slice(0, 6);

        recentGrid.innerHTML = recentPages.map(page => `
            <a href="${page.url}" class="wiki-page-card">
                <h3>${page.title}</h3>
                <p>${page.excerpt}</p>
                <div class="wiki-page-meta">
                    ${page.tags.map(tag => `<span class=\"wiki-tag\" role=\"link\" tabindex=\"0\" onclick=\"event.preventDefault(); event.stopPropagation(); window.location.href='${(this.config.baseUrl || '').replace(/\/$/, '')}/wiki/tag/${encodeURIComponent(tag)}/'\" onkeydown=\"if(event.key==='Enter'||event.key===' '){event.preventDefault(); event.stopPropagation(); window.location.href='${(this.config.baseUrl || '').replace(/\/$/, '')}/wiki/tag/${encodeURIComponent(tag)}/'}\">${tag}</span>`).join('')}
                </div>
            </a>
        `).join('');
    }

    renderAllPages() {
        const pagesList = document.getElementById('wiki-pages-list');
        if (!pagesList) return;

        const sortedPages = this.wikiPages.sort((a, b) => a.title.localeCompare(b.title));

        pagesList.innerHTML = sortedPages.map(page => `
            <a href="${page.url}" class="wiki-page-item">
                <h3>${page.title}</h3>
                <span class="wiki-page-date">${new Date(page.published_at).toLocaleDateString()}</span>
            </a>
        `).join('');
        this.updatePagesCount(sortedPages.length, this.wikiPages.length);
    }

    injectWikiIndexMarkdown() {
        // Find the wiki-index page from markdown data and inject into the content area on /wiki/
        const contentEl = document.getElementById('wiki-content');
        if (!contentEl) return;
        const indexPage = this.wikiPages.find(p => p.url === '/wiki/' || p.slug === 'wiki-index');
        if (!indexPage) return;
        // Set title and content
        const titleEl = document.getElementById('wiki-title');
        if (titleEl && indexPage.title) titleEl.textContent = indexPage.title;
        contentEl.innerHTML = (indexPage.html || indexPage.content || '').trim() || '<p>Welcome to the Wiki.</p>';
        // Remove duplicate H1 inside injected content so the title shows only once
        try {
            const firstHeading = contentEl.querySelector('h1');
            if (firstHeading) firstHeading.remove();
        } catch {}
        // Update meta info
        const lastUpdated = document.getElementById('wiki-last-updated');
        if (lastUpdated) {
            const date = new Date(indexPage.updated_at || indexPage.published_at || Date.now());
            lastUpdated.textContent = `Last updated: ${date.toLocaleDateString()}`;
        }
        const tagsEl = document.getElementById('wiki-tags');
        if (tagsEl && Array.isArray(indexPage.tags)) {
            const base = (this.config.baseUrl || '').replace(/\/$/, '');
            tagsEl.innerHTML = indexPage.tags.map(t => `<a class="wiki-tag" href="${base}/wiki/tag/${encodeURIComponent(t)}/">${t}</a>`).join('');
        }
    }

    renderEmptyState() {
        const recentGrid = document.getElementById('wiki-recent-grid');
        const pagesList = document.getElementById('wiki-pages-list');
        const categories = document.getElementById('wiki-categories');
        const message = '<p class="wiki-no-results">No wiki pages found. Check that built/wiki-data.json is deployed and clear your cache.</p>';
        if (recentGrid) recentGrid.innerHTML = message;
        if (pagesList) pagesList.innerHTML = message;
        if (categories) categories.innerHTML = '';
    }

    filterByCategory(category) {
        this.activeCategoryFilter = category;
        // Update active badge
        document.querySelectorAll('.wiki-category').forEach(cat => cat.classList.remove('active'));
        const badge = document.querySelector(`[data-category="${category}"]`);
        if (badge) badge.classList.add('active');

        const filteredPages = this.wikiPages.filter(page => Array.isArray(page.tags) && page.tags.includes(category));
        this.updateRecentGrid(filteredPages);
        this.updatePagesList(filteredPages);
        this.updatePagesCount(filteredPages.length, this.wikiPages.length);
    }

    clearCategoryFilter() {
        this.activeCategoryFilter = null;
        document.querySelectorAll('.wiki-category').forEach(cat => cat.classList.remove('active'));
        this.showAllPages();
    }

    updateRecentGrid(pages) {
        const recentGrid = document.getElementById('wiki-recent-grid');
        if (!recentGrid) return;

        const recentPages = pages
            .sort((a, b) => new Date(b.published_at) - new Date(a.published_at))
            .slice(0, 6);

        recentGrid.innerHTML = recentPages.map(page => `
            <a href="${page.url}" class="wiki-page-card">
                <h3>${page.title}</h3>
                <p>${page.excerpt}</p>
                <div class="wiki-page-meta">
                    ${page.tags.map(tag => `<span class="wiki-tag" role="link" tabindex="0" onclick="event.preventDefault(); event.stopPropagation(); window.location.href='${(this.config.baseUrl || '').replace(/\/$/, '')}/wiki/tag/${encodeURIComponent(tag)}/'" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault(); event.stopPropagation(); window.location.href='${(this.config.baseUrl || '').replace(/\/$/, '')}/wiki/tag/${encodeURIComponent(tag)}/'}">${tag}</span>`).join('')}
                </div>
            </a>
        `).join('');
    }

        updatePagesList(pages) {
        const pagesList = document.getElementById('wiki-pages-list');
        if (!pagesList) return;

        const sortedPages = pages.sort((a, b) => a.title.localeCompare(b.title));

        pagesList.innerHTML = sortedPages.map(page => `
            <a href="${page.url}" class="wiki-page-item">
                <h3>${page.title}</h3>
                <div class="wiki-page-right">
                    <span class="wiki-page-date">${new Date(page.published_at).toLocaleDateString()}</span>
                    <div class="wiki-tags">
                        ${(page.tags || []).map(tag => `<span class="wiki-tag" role="link" tabindex="0" onclick="event.preventDefault(); event.stopPropagation(); window.location.href='${(this.config.baseUrl || '').replace(/\/$/, '')}/wiki/tag/${encodeURIComponent(tag)}/'" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault(); event.stopPropagation(); window.location.href='${(this.config.baseUrl || '').replace(/\/$/, '')}/wiki/tag/${encodeURIComponent(tag)}/'}">${tag}</span>`).join('')}
                    </div>
                </div>
            </a>
        `).join('');
        this.updatePagesCount(sortedPages.length, this.wikiPages.length);
    }

    updatePagesCount(visibleCount, totalCount) {
        const allPagesSection = document.querySelector('.wiki-all-pages .wiki-section-title');
        if (!allPagesSection) return;
        if (typeof visibleCount === 'number' && typeof totalCount === 'number') {
            if (visibleCount === totalCount) {
                allPagesSection.textContent = `All Pages (${totalCount})`;
            } else {
                allPagesSection.textContent = `All Pages (${visibleCount} of ${totalCount})`;
            }
        }
    }
}

// Initialize wiki systems (supports late-loaded modules after DOMContentLoaded)
const initializeWikiModules = () => {
    const hasIndex = !!document.querySelector('.wiki-index-container');
    const hasWikiContainer = !!document.querySelector('.wiki-container');

    // Initialize page wiki system only when not on the index template
    if (hasWikiContainer && !hasIndex) {
        const isMarkdownMode = !!(window.wikiConfig && window.wikiConfig.dataUrl);
        if (!isMarkdownMode) {
            new WikiSystem();
        }
    }
    if (hasIndex) {
        new WikiIndexSystem();
    }
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWikiModules, { once: true });
} else {
    // DOM is already ready, run immediately
    initializeWikiModules();
}

// Toggle for SSR links section at bottom (All Wiki Links)
try {
    const onReady = (cb) => {
        if (document.readyState === 'complete' || document.readyState === 'interactive') cb();
        else document.addEventListener('DOMContentLoaded', cb, { once: true });
    };
    onReady(() => {
        const toggle = document.querySelector('.wiki-pages-toggle');
        const content = document.getElementById('wiki-ssr-links');
        if (toggle && content) {
            const setState = (expanded) => {
                toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
                content.style.display = expanded ? 'block' : 'none';
            };
            setState(false);
            toggle.addEventListener('click', () => {
                const expanded = toggle.getAttribute('aria-expanded') === 'true';
                setState(!expanded);
            });
        }
    });
} catch {}
